import { pageCreditLabelKey } from '@/components/contributor/credit-label';
import ResourcesCarousel from '@/components/resources-carousel';
import { useCurrentLanguage } from '@/hooks/use-current-language';
import useIsContentLocked from '@/hooks/use-is-content-locked';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { usePollResponse } from '@/hooks/use-poll-response';
import { logger } from '@/libs/logger';
import { SupportedLanguageType } from '@/utils/intl';
import { MediaGuideMetadata } from '@/utils/media/types';
import { EnumButtonName } from '@calm/evexia-analytics';
import {
    WorkflowStepDataContentPack,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import { Check } from '@mui/icons-material';
import { LoadingDots } from '@rhg/components/animations/loading-dots';
import { ContributorDialog } from '@rhg/components/contributor/dialog';
import { ContributorListItem } from '@rhg/components/contributor/list-item';
import {
    ContributorSelection,
    ContributorType,
} from '@rhg/components/contributor/types';
import { AllGuidesCompleted } from '@rhg/components/guide/completed-dialogs/all-guides-completed';
import { TrailerCard } from '@rhg/components/guide/horizontal-minimal-card';
import { GuideListItem } from '@rhg/components/guide/list-item';
import { resolveCreditGroups } from '@rhg/components/media/audio-player/credits';
import { largeButtonCardWidth } from '@rhg/components/pack-page/pack-carousel-section';
import { PollQuestionResponse } from '@rhg/components/poll/question';
import { useShuffleArm } from '@rhg/components/program/shuffle-arm';
import HasResourcesConfigured from '@rhg/components/resources/hasResourcesConfigured/index';
import useConfirmation from '@rhg/hooks/use-confirmation';
import { usePoll } from '@rhg/hooks/use-poll';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useUnsponsoredDialog } from '@rhg/hooks/use-unsponsored-dialog';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    Button,
    Divider,
    Grid,
    Paper,
    Stack,
    Theme,
    Typography,
    useTheme,
} from '@rhg/libs/material';
import { LockThinOutline } from '@rhg/libs/material/icons';
import { styleIconLarge } from '@rhg/libs/material/sx';
import { useAudioControls } from '@rhg/providers/guide-player/active-player/use-audio-controls';
import { shuffleCapabilityForControls } from '@rhg/providers/guide-queue/capabilities';
import { displayLocaleDateString } from '@rhg/utils/date-time';
import { getMediaType, MediaType } from '@rhg/utils/media';
import { ProgramState } from '@rhg/utils/programs';
import { parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ProgramNextButton } from '../next-button';
import { NextButtonOnClickHandler } from '../next-button/types';
import { PacksCarouselSection } from '../packs-carousel-section';
import { HeaderPlaybackControls } from '../program-header/header-playback-controls';

const MAX_RELATED_COUNT = 9;

const styleCompletedContainer = {
    mb: 6,
    padding: 6,
};

const styleCompletedIconContainer = {
    borderRadius: '50%',
    color: 'primary.main',
    padding: 3,
    width: '64px',
    height: '64px',
    mb: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.info.light
            : theme.palette.info.dark,
};

const styleCompletedIcon = styleIconLarge;

const styleCompletedTitle = {
    mt: 4,
    fontWeight: (theme: Theme) => theme.typography.fontWeightBold,
};

const styleCompletedBody = {
    mt: 2,
    color: 'text.sub',
};
const styleDividerTopSpacing = { width: '100%', mt: 4 };
const styleDivider = {
    width: '100%',
    my: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleNextButton = { width: '100%' };
const styleGuideList = {
    p: 0,
    m: 0,
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

function RatingButton({ npsPollId }: { npsPollId: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const { formatMessage } = useIntl();

    const handleClose = () => setIsOpen(false);
    const handleOpen = () => setIsOpen(true);

    return (
        <>
            <Stack alignItems="center">
                <Box>
                    <Button
                        trackButton="NotMapped"
                        variant="outlined"
                        size="small"
                        sx={{ px: '32px' }}
                        onClick={handleOpen}
                    >
                        {formatMessage(programMessages['program.rate_program'])}
                    </Button>
                </Box>
            </Stack>
            {isOpen && (
                <AllGuidesCompleted
                    isOpen
                    onClose={handleClose}
                    npsPollId={npsPollId}
                    showUpdatePlan={false}
                />
            )}
        </>
    );
}

function CompletionInfo({ workflow }: { workflow?: WorkflowWorkflowInfo }) {
    const { formatMessage } = useIntl();
    const data = workflow?.steps[0].stepDefn
        .data as WorkflowStepDataContentPack;
    const { npsPollId } = data;
    const { pollResponse, isLoading: isLoadingResponse } =
        usePollResponse(npsPollId);
    const { poll, isLoading: isLoadingPoll } = usePoll(npsPollId);

    const formattedCompletionDate = useMemo(
        () =>
            workflow?.workflowInstance?.completedAt &&
            displayLocaleDateString(
                parseISO(workflow?.workflowInstance?.completedAt)
            ),
        [workflow?.workflowInstance?.completedAt]
    );

    if (isLoadingResponse || isLoadingPoll) {
        return <LoadingDots />;
    }

    const answer = pollResponse?.polls?.[0]?.questions?.[0]?.answer;
    const rating = answer?.range;
    const question = poll?.questions?.[0];

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="subtitle2" sx={styleCompletedBody}>
                    {formatMessage(
                        programMessages['program.program_completed_body'],
                        {
                            date: formattedCompletionDate,
                        }
                    )}{' '}
                    {rating &&
                        formatMessage(
                            programMessages['program.program_completed_rated']
                        )}
                </Typography>
            </Stack>
            {question &&
                (rating ? (
                    <PollQuestionResponse question={question} answer={answer} />
                ) : (
                    <RatingButton npsPollId={npsPollId} />
                ))}
        </Stack>
    );
}
interface ProgramScheduleProps {
    onJoin: (guide: MediaGuideMetadata) => void;
}
export function ProgramSchedule({ onJoin }: ProgramScheduleProps) {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const {
        orderedSessions,
        packClass,
        program,
        programState,
        programType,
        workflow,
    } = useProvidedProgramContextWithGuidesAndWorkflow();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const { showRegistrationDialog } = useRegistrationDialog();
    const { handleNextButton, playGuide } = useProgramActions();
    const { isShuffleArmed, setShuffleArmed } = useShuffleArm();
    const { confirm } = useConfirmation();
    const { formatMessage } = useIntl();
    const { getIsContentLocked } = useIsContentLocked();
    const currentLanguage = useCurrentLanguage();

    const isContentLocked = getIsContentLocked({
        programState,
        programType,
    });

    const canInteract = useMemo(
        () =>
            programType !== 'clinical_program' ||
            programState === ProgramState.InProgress,
        [programState, programType]
    );

    const handleUnsponsored = useCallback(async () => {
        if (await showUnsponsoredDialog()) {
            showRegistrationDialog({
                currentStep: RegistrationFlowStep.AccessCode,
                registrationFlow: RegistrationFlow.ExistingRegistration,
            }).catch((err) =>
                logger.error(
                    'Failed to show registration dialog',
                    {},
                    err as Error
                )
            );
        }
    }, [showUnsponsoredDialog, showRegistrationDialog]);

    const handleNextButtonSchedule: NextButtonOnClickHandler = (args) => {
        if (isContentLocked) {
            handleUnsponsored().catch((err) =>
                logger.error(
                    'Failed to show unsponsored dialog',
                    {},
                    err as Error
                )
            );
        } else {
            handleNextButton()(args);
        }
    };

    const handleGuide = useCallback(
        async (guide: MediaGuideMetadata) => {
            if (isContentLocked) {
                await handleUnsponsored();
            } else if (canInteract) {
                // EVE-13091: if shuffle is armed on the detail page, picking a
                // track keeps shuffle on and starts from that track (native
                // parity). Consume the armed intent here.
                if (isShuffleArmed) {
                    playGuide(guide, { willShuffle: true });
                    setShuffleArmed(false);
                } else {
                    playGuide(guide);
                }
            } else if (
                await confirm({
                    noCancelButton: true,
                    icon: <LockThinOutline fontSize="large" color="inherit" />,
                    iconColor: 'info',
                    title: formatMessage(
                        programMessages['program.join_program']
                    ),
                    body: formatMessage(
                        programMessages['program.before_viewing_join']
                    ),
                    confirmLabel: formatMessage(
                        programMessages['program.join_program']
                    ),
                    trackDialog: 'JoinBeforeViewingProgram',
                    confirmTrackButton: 'DialogJoinProgram',
                })
            ) {
                onJoin(guide);
            }
        },
        [
            canInteract,
            confirm,
            handleUnsponsored,
            isContentLocked,
            isShuffleArmed,
            onJoin,
            playGuide,
            setShuffleArmed,
            formatMessage,
        ]
    );

    const trailer = useMemo(
        () => orderedSessions?.find((guide) => guide.isTrailer),
        [orderedSessions]
    );

    const nonTrailerGuides = useMemo(
        () => orderedSessions?.filter((guide) => !guide.isTrailer),
        [orderedSessions]
    );

    const firstGuide = nonTrailerGuides?.[0] ?? orderedSessions?.[0];
    const audioControls = useAudioControls({
        program,
        playingGuide: firstGuide ?? ({} as MediaGuideMetadata),
    });
    // Gates the playlist-style header layout (Play/Shuffle CTAs + attribution
    // rows) for playlist/album/DTC programs. shuffleCapabilityForControls is
    // true only for the Playlist control type, so this really means "this is a
    // playlist-type program that gets the Play/Shuffle layout."
    const isPlaylistProgram =
        !!firstGuide && shuffleCapabilityForControls(audioControls);

    // Attribution rows mirror the player's credit rules (composer→Artist,
    // collapse a same-person narrator/author). Page wording keeps "Clinical
    // Author" via pageCreditLabelKey (EVE-11935).
    const creditGroups = useMemo(
        () =>
            resolveCreditGroups(
                audioControls,
                program?.narrator,
                program?.author
            ),
        [audioControls, program?.narrator, program?.author]
    );

    const [selectedContributor, setSelectedContributor] =
        useState<ContributorSelection>();

    const relatedPacks = useMemo(
        () =>
            workflow?.steps
                .map((step) =>
                    'supplementalIds' in step.stepDefn.data
                        ? step.stepDefn.data.supplementalIds
                        : []
                )
                .flat(),
        [workflow]
    );
    const isVideo = useMemo(
        () => getMediaType(nonTrailerGuides[0]) === MediaType.Video,
        [nonTrailerGuides]
    );

    const isCompleted = programState === ProgramState.Completed;
    const shouldShowRelatedPacks =
        relatedPacks &&
        relatedPacks?.length &&
        currentLanguage === SupportedLanguageType.EN;

    return (
        <Grid
            container
            justifyContent="center"
            mb={theme.ds.spacingUnits.SECTION}
        >
            {isCompleted && programType === 'clinical_program' && (
                <Grid item xs={12}>
                    <Paper sx={styleCompletedContainer}>
                        <Stack alignItems="center">
                            <Box sx={styleCompletedIconContainer}>
                                <Check sx={styleCompletedIcon} color="info" />
                            </Box>
                            <Typography
                                variant="body1"
                                sx={styleCompletedTitle}
                            >
                                {formatMessage(
                                    programMessages[
                                        'program.program_completed_title'
                                    ]
                                )}
                            </Typography>
                            <CompletionInfo workflow={workflow} />
                        </Stack>
                    </Paper>
                </Grid>
            )}
            {isPlaylistProgram ? (
                <>
                    <Grid item xs={12}>
                        {creditGroups.map((group) => {
                            const contributor = group.members[0];
                            if (!contributor) return null;
                            const contributorType =
                                group.role === 'author'
                                    ? ContributorType.Author
                                    : ContributorType.Narrator;
                            return (
                                <ContributorListItem
                                    key={group.labelKey}
                                    clickable
                                    trackButton={
                                        group.role === 'author'
                                            ? 'ViewProgramAuthor'
                                            : 'ViewProgramNarrator'
                                    }
                                    contributor={contributor}
                                    contributorType={contributorType}
                                    roleLabel={formatMessage(
                                        commonMessages[
                                            pageCreditLabelKey(group.labelKey)
                                        ]
                                    )}
                                    onClick={() =>
                                        setSelectedContributor({
                                            contributor,
                                            contributorType,
                                        })
                                    }
                                />
                            );
                        })}
                    </Grid>
                    {isMobile && (
                        <>
                            <Grid item xs={12}>
                                <Divider
                                    aria-hidden
                                    orientation="horizontal"
                                    sx={styleDivider}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <HeaderPlaybackControls
                                    guides={orderedSessions}
                                    packClass={packClass}
                                    program={program}
                                    onNextButtonClick={handleNextButton()}
                                    playTrackButton={
                                        EnumButtonName.ProgramHeaderNext
                                    }
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <Divider
                            aria-hidden
                            orientation="horizontal"
                            sx={styleDividerTopSpacing}
                        />
                    </Grid>
                </>
            ) : isMobile ? (
                <>
                    <Grid item md={9} sm={12}>
                        <ProgramNextButton
                            trackButton="ProgramScheduleNext"
                            onClick={handleNextButtonSchedule}
                            sx={styleNextButton}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Divider
                            aria-hidden
                            orientation="horizontal"
                            sx={styleDivider}
                        />
                    </Grid>
                </>
            ) : null}
            {trailer && (
                <Grid item xs={12}>
                    <TrailerCard
                        guide={trailer}
                        onPlay={() =>
                            playGuide(trailer, {
                                willSkipToPreviousProgress: false,
                            })
                        }
                        preferNarratorImage={!isVideo}
                    />
                    <Divider
                        aria-hidden
                        sx={styleDivider}
                        orientation="horizontal"
                    />
                </Grid>
            )}
            <Grid item xs={12} component="ul" sx={styleGuideList}>
                {nonTrailerGuides.map((guide, i) => (
                    <GuideListItem
                        key={i}
                        guide={guide}
                        programState={programState}
                        programType={programType}
                        onJoin={onJoin}
                        onClick={(guide) => {
                            handleGuide(guide).catch((err) =>
                                logger.error(
                                    'Failed to handle guide click',
                                    {},
                                    err as Error
                                )
                            );
                        }}
                    />
                ))}
            </Grid>
            <HasResourcesConfigured withThemes={true} themes={workflow?.themes}>
                <ResourcesCarousel
                    variant="smart"
                    themes={workflow?.themes}
                    source="ProgramSchedule"
                />
            </HasResourcesConfigured>
            {shouldShowRelatedPacks ? (
                <PacksCarouselSection
                    cardWidth={largeButtonCardWidth}
                    title={formatMessage(programMessages['program.related'])}
                    packClasses={relatedPacks}
                    maxCardLimit={MAX_RELATED_COUNT}
                />
            ) : null}
            {/*pollWorkflowSteps?.map((pollStep, i) => (
                <Grid key={i} item xs={12}>
                    <PollStepCard
                        step={pollStep}
                        sx={stylePollCard}
                        onClick={handleWorkflowStep}
                    />
                </Grid>
                ))*/}
            <ContributorDialog
                isOpen={!!selectedContributor}
                contributor={selectedContributor?.contributor}
                contributorType={selectedContributor?.contributorType}
                onClose={() => setSelectedContributor(undefined)}
            />
        </Grid>
    );
}
